import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ROLES = ['Software Engineer', 'AI Builder', 'Full-Stack Dev', '10× Engineer'];
const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const ScrambleText = ({ text, className, delay = 0 }) => {
  const [display, setDisplay] = useState(() => text.replace(/[A-Za-z]/g, '?'));

  useEffect(() => {
    let frame = 0;
    let raf;
    const totalFrames = text.length * 6;

    const tick = () => {
      setDisplay(
        text.split('').map((char, i) => {
          if (char === ' ') return ' ';
          if (i < Math.floor(frame / 6)) return char;
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        }).join('')
      );
      frame++;
      if (frame <= totalFrames) raf = requestAnimationFrame(tick);
      else setDisplay(text);
    };

    const t = setTimeout(() => { raf = requestAnimationFrame(tick); }, delay);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [text, delay]);

  return <span className={className}>{display}</span>;
};

const useCountUp = (target, duration = 1500) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || fired.current) return;
      fired.current = true;
      let startTime;
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target));
        if (p < 1) requestAnimationFrame(step);
        else setCount(target);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return { count, ref };
};

const StatCounter = ({ value, suffix, label }) => {
  const { count, ref } = useCountUp(value);
  return (
    <div ref={ref} className="hero-stat">
      <span className="stat-num">{count}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
};

const Hero = () => {
  const [roleIdx, setRoleIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx(i => (i + 1) % ROLES.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="hero" className="hero-section">
      <div className="hero-blob hero-blob--1" />
      <div className="hero-blob hero-blob--2" />

      <div className="hero-inner">
        {/* LEFT */}
        <motion.div
          className="hero-left"
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75, ease: 'easeOut', delay: 0.1 }}
        >
          <div className="hero-mobile-top">
            <img src="profile-image.png" alt="Harsh Singh" className="hero-mobile-avatar" />
            <span className="hero-greeting">Hi, I'm</span>
          </div>

          <h1 className="hero-name">
            <ScrambleText text="Harsh" className="hero-name-line" delay={300} />
            <ScrambleText text="Singh" className="hero-name-line gradient-text" delay={600} />
          </h1>

          <p className="hero-tagline">
            Building AI-native tools that ship fast and scale further.
          </p>

          <div className="hero-role-track">
            <AnimatePresence mode="wait">
              <motion.span
                key={roleIdx}
                className="hero-role"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.35 }}
              >
                {ROLES[roleIdx]}
              </motion.span>
            </AnimatePresence>
          </div>

          <div className="hero-stats">
            <StatCounter value={80} suffix="%" label="faster builds" />
            <div className="stat-divider" />
            <StatCounter value={30} suffix="%" label="premium growth" />
            <div className="stat-divider" />
            <StatCounter value={10} suffix="×" label="engineer" />
          </div>

          <div className="hero-actions">
            <a href="#projects" className="btn-primary">View Work</a>
            <a href="#contact" className="btn-ghost">Get in Touch</a>
          </div>

          <div className="hero-socials">
            <a href="https://www.linkedin.com/in/harshxdev/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24"><path d="M23.994 24v-.001H24v-8.802c0-4.306-.927-7.623-5.961-7.623-2.42 0-4.044 1.328-4.707 2.587h-.07V7.976H8.489v16.023h4.97v-7.934c0-2.089.396-4.109 2.983-4.109 2.549 0 2.587 2.384 2.587 4.243V24zM.396 7.977h4.976V24H.396zM2.882 0C1.291 0 0 1.291 0 2.882s1.291 2.909 2.882 2.909 2.882-1.318 2.882-2.909A2.884 2.884 0 002.882 0z" /></svg>
            </a>
            <a href="https://github.com/HARSHSINGH0" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <svg viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>
            </a>
          </div>

          <div className="hero-stack">
            {['React', 'Next.js', 'TypeScript', 'Node.js', 'LangGraph', 'Electron'].map(t => (
              <span key={t} className="hero-stack-pill">{t}</span>
            ))}
          </div>
        </motion.div>

        {/* RIGHT */}
        <motion.div
          className="hero-right"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.75, ease: 'easeOut', delay: 0.3 }}
        >
          <div className="hero-image-wrap">
            <div className="hero-image-ring" />
            <img src="profile-image.png" alt="Harsh Singh" className="hero-avatar" />
            <a
              href="https://www.linkedin.com/posts/harshxdev_dronahq-starperformer2024-gratitude-activity-7301443583188832256-8WuU"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-award-badge"
            >
              <svg className="badge-trophy" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 3h10v6a5 5 0 01-10 0V3z" fill="#D97706"/>
                <path d="M4 3c0 3.2 1.3 4.5 3 5M20 3c0 3.2-1.3 4.5-3 5" stroke="#B45309" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M4 3H7M17 3h3" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M10 14v3M14 14v3" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M8 17h8" stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>
                <path d="M12 5l.8 2.2H15l-1.8 1.3.7 2.2L12 9.5l-1.9 1.2.7-2.2L9 7.2h2.2L12 5z" fill="#FCD34D"/>
              </svg>
              <div className="badge-text">
                <span className="badge-title">Star Performer</span>
                <span className="badge-sub">DronaHQ</span>
              </div>
            </a>
          </div>
        </motion.div>
      </div>

      <div className="hero-scroll-hint">
        <span>Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
};

export default Hero;
